/**
 * Пометки анти-скама так, чтобы их прочли не наводя курсор.
 *
 * Сервер присылает готовый текст с числами этой вилки — он подробный и это
 * правильно. Но в строке ленты подробному тексту места нет, и до сих пор там
 * стоял счётчик «⚠ 2», а причина пряталась в `title`. Наведение курсора — не
 * способ читать: на телефоне его нет вовсе, а лента просматривается по диагонали
 * за секунды. Счётчик пометок при этом сообщает ровно ноль полезного: «протухшая
 * котировка» и «нет прямой ссылки» — это разные решения, а не «две проблемы».
 *
 * Поэтому здесь короткое слово на код правила и порядок тяжести: в строку идёт
 * САМОЕ дорогое из сработавших, остальные счётчиком рядом. Полный текст сервера
 * остаётся в раскрытой карточке и в подсказке.
 */

import type { ForkRule } from './protocol';

/**
 * Порядок — по цене ошибки, а не по алфавиту. Первое в списке дороже: по
 * протухшей котировке ставку просто не примут, а отсутствующая ссылка означает
 * лишь минуту поиска матча руками.
 */
const SEVERITY: string[] = [
  'stale',
  'suspended',
  // Пометки второго контура. `frozen` стоит выше `odds_anomaly` не по симметрии:
  // в чужой ленте у ноги НЕТ времени котировки, и неподвижность цены — единственное,
  // что отличает живую вилку от их залипшей записи. Это замена `stale`, а не довесок.
  'frozen',
  // `leg_dead` стоит рядом с `frozen` и выше его быть не должен: у чужой ленты
  // цена стоит у ВСЕЙ вилки, а мёртвая нога — наше собственное наблюдение по
  // каждой ноге, и в строке конкурента ей взяться неоткуда.
  'leg_dead',
  'odds_anomaly',
  'unverifiable',
  'thin',
  'one_sided',
  'delayed',
  'expensive_share',
  'too_old',
  'no_link',
  // Оговорки адаптера — в самом низу тяжести намеренно. Цена на такой строке
  // настоящая, и рынок опубликован именно потому, что отказываться от него
  // целиком дороже, чем подписать. Встань они выше, слово «овертайм?» вытесняло
  // бы из строки «протухла» — то есть предупреждение о непроверенной детали
  // заслоняло бы предупреждение о том, что ставку не примут вовсе.
  'ot_unverified',
  'unit_unverified',
];

/** Код правила → слово в строку. Незнакомый код печатается как есть. */
const SHORT: Record<string, string> = {
  stale: 'протухла',
  delayed: 'задержка',
  suspended: 'линия снята',
  no_link: 'нет ссылки',
  one_sided: 'полстакана',
  expensive_share: 'дорогой шар',
  odds_anomaly: 'опечатка?',
  frozen: 'цена стоит',
  statue: 'труп',
  leg_dead: 'нога мертва',
  expired: 'висит слишком долго',
  draw_possible: 'ничья не закрыта',
  unverifiable: 'вход не проверить',
  too_old: 'висит давно',
  thin: 'не влезает',
  // Вопросительный знак — часть слова, а не украшение: он говорит, что это наше
  // незнание, а не свойство вилки. «Овертайм?» читается как «уточни у конторы,
  // считает ли она овертайм», и это ровно то решение, которое остаётся человеку.
  ot_unverified: 'овертайм?',
  unit_unverified: 'период?',
};

export const ruleShort = (code: string): string => SHORT[code] ?? code;

/** Самое дорогое из сработавших правил. Неизвестные коды идут после известных. */
export function worstRule(rules: ForkRule[]): ForkRule | null {
  if (rules.length === 0) return null;
  const rank = (rule: ForkRule): number => {
    const index = SEVERITY.indexOf(rule.c);
    return index === -1 ? SEVERITY.length : index;
  };
  return rules.reduce((worst, rule) => (rank(rule) < rank(worst) ? rule : worst));
}

/**
 * Подпись бейджа: слово вместо числа.
 *
 * Остальные пометки показываются счётчиком «+N», а не перечислением: строка
 * ленты одна, и уместить в неё три причины значит не уместить ни одной.
 *
 * Приговор сервера (`flagged`) главнее списка правил: если пометка стоит, а
 * текста к ней не приехало, на экране всё равно обязан быть знак. «Чисто» на
 * помеченной вилке — это не косметика, это ставка, которой человек не сделал бы.
 */
export function badgeText(rules: ForkRule[], flagged: boolean): string {
  const worst = worstRule(rules);
  if (worst === null) return flagged ? '⚠ пометка' : 'чисто';
  const rest = rules.length - 1;
  return rest > 0 ? `⚠ ${ruleShort(worst.c)} +${rest}` : `⚠ ${ruleShort(worst.c)}`;
}

/** Полные тексты сервера — в подсказку целиком, без обрезки. */
export const rulesHint = (rules: ForkRule[]): string =>
  rules.length === 0 ? 'правила анти-скама молчат' : rules.map((rule) => rule.t).join('\n');
